'use client';
import { JSX, useEffect, useRef } from 'react';
import classNames from 'classnames';

// components
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';

// utils
import { AnimatePresence, motion } from 'framer-motion';

// types
import { EIcons } from '@/components/atoms/at-icon/icons';
import { IMlExteriorDrawerProps } from './ml-exterior-drawer.types';

/**
 * `MlExteriorDrawer` is an animated, mobile-friendly bottom drawer component used
 * to display supplemental UI like selectors, filters, or form content.
 *
 * It leverages Framer Motion for smooth entrance/exit transitions and includes full
 * accessibility support: focus trap, ESC-to-close, and scroll locking on the body.
 *
 * - When `isOpen` is `true`, the drawer appears with a backdrop.
 * - Focus is manually trapped inside the drawer to prevent tab escape.
 * - A close button in the top-right allows manual dismissal.
 *
 */
export const MlExteriorDrawer = ({
  bgColor = 'bg-skin-primary',
  children,
  isOpen,
  subtitle,
  title,
  onClose,
}: IMlExteriorDrawerProps): JSX.Element | null => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen || !drawerRef.current) return;
    const drawerElement = drawerRef.current;

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.shiftKey) return;

      const elementsArray = [
        ...((drawerElement?.querySelectorAll(
          'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
        ) as NodeListOf<HTMLElement>) || []),
      ];

      if (elementsArray.length === 0) return;

      const lastElement = elementsArray[elementsArray.length - 1];

      // if the user tabs to the last focusable element, move focus to the close button
      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        closeBtnRef.current?.focus();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    drawerElement.addEventListener('keydown', handleTab);
    return () => {
      window.removeEventListener('keydown', handleEscape);
      drawerElement?.removeEventListener('keydown', handleTab);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed inset-0 z-40 bg-black/30"
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: '0%' }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className={classNames(
              'scrollbar-hidden fixed bottom-0 left-0 z-50 max-h-[80vh] w-full overflow-y-auto rounded-t-3xl shadow-lg',
              bgColor,
            )}
          >
            <div className="px-4 pb-10 pt-8">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <AtHeader element="h4" className="mb-2">
                    {title}
                  </AtHeader>
                  {subtitle && (
                    <AtText className="font-Thermal">{subtitle}</AtText>
                  )}
                </div>

                <button
                  aria-label="close"
                  ref={closeBtnRef}
                  className="text-44"
                  onClick={onClose}
                >
                  <AtIcon
                    className="rounded-full bg-skin-ui-fill-primary"
                    icon={EIcons.NAV_CLOSE}
                  />
                </button>
              </div>

              {/* Content */}
              <div>{children}</div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
